export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Profile photos are 4:5 portrait — 1080×1350. */
export const PROFILE_PHOTO_WIDTH = 1080;
export const PROFILE_PHOTO_HEIGHT = 1350;
export const PROFILE_PHOTO_ASPECT = PROFILE_PHOTO_WIDTH / PROFILE_PHOTO_HEIGHT;
/** @deprecated Use PROFILE_PHOTO_WIDTH. Kept as the 1080px width alias. */
export const PROFILE_PHOTO_SIZE = PROFILE_PHOTO_WIDTH;

interface CoverCropOptions {
  videoWidth: number;
  videoHeight: number;
  videoRect: CropRect;
  frameRect: CropRect;
  mirrored?: boolean;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export function isNormalizedProfilePhoto(width: number, height: number): boolean {
  return width === PROFILE_PHOTO_WIDTH && height === PROFILE_PHOTO_HEIGHT;
}

/**
 * Strictly bound a source rectangle so ctx.drawImage() cannot throw IndexSizeError
 * on high-DPI WebViews when float rounding overshoots the bitmap.
 */
export function clampSourceRect(
  sourceX: number,
  sourceY: number,
  sourceWidth: number,
  sourceHeight: number,
  mediaWidth: number,
  mediaHeight: number
): CropRect {
  const width = clamp(sourceWidth, 0, mediaWidth);
  const height = clamp(sourceHeight, 0, mediaHeight);
  const x = clamp(sourceX, 0, mediaWidth - width);
  const y = clamp(sourceY, 0, mediaHeight - height);

  return {
    x,
    y,
    width: Math.min(width, mediaWidth - x),
    height: Math.min(height, mediaHeight - y),
  };
}

/**
 * Front-facing / selfie previews are CSS-mirrored. Rear cameras stay LTR.
 * Unknown facing modes default to mirrored because the live preview is a selfie.
 */
export function isMirroredPreview(facingMode?: string | null): boolean {
  return facingMode !== 'environment';
}

/**
 * Maps a frame drawn over an object-fit: cover video back to source-video pixels.
 * The guide must be the same 4:5 box the canvas will render (1080×1350).
 */
export function calculateCoverSourceCrop({
  videoWidth,
  videoHeight,
  videoRect,
  frameRect,
  mirrored = false,
}: CoverCropOptions): CropRect {
  if (
    videoWidth <= 0 ||
    videoHeight <= 0 ||
    videoRect.width <= 0 ||
    videoRect.height <= 0 ||
    frameRect.width <= 0 ||
    frameRect.height <= 0
  ) {
    throw new Error('Camera and crop dimensions must be positive');
  }

  // object-fit: cover + object-position: center
  const coverScale = Math.max(
    videoRect.width / videoWidth,
    videoRect.height / videoHeight
  );
  const renderedWidth = videoWidth * coverScale;
  const renderedHeight = videoHeight * coverScale;
  const hiddenInline = (renderedWidth - videoRect.width) / 2;
  const hiddenBlock = (renderedHeight - videoRect.height) / 2;

  // Keep the 4:5 guide ratio — do not clamp width/height independently here.
  const sourceWidth = frameRect.width / coverScale;
  const sourceHeight = frameRect.height / coverScale;
  const unmirroredX =
    (frameRect.x - videoRect.x + hiddenInline) / coverScale;
  const sourceY =
    (frameRect.y - videoRect.y + hiddenBlock) / coverScale;
  const sourceX = mirrored
    ? videoWidth - unmirroredX - sourceWidth
    : unmirroredX;

  return clampSourceRect(
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    videoWidth,
    videoHeight
  );
}
